import { Maybe } from '@varo/graphql-schema-types';
import React, { useState } from 'react';
import { Platform, StyleSheet, View, ViewStyle } from 'react-native';
import PressableWithDelay from 'shared/components/PressableWithDelay/PressableWithDelay';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import ListItemLeadingAccessory, {
  ListItemLeadingAccessoryProps,
} from 'shared/components/vxl/ListItem/ListItemLeadingAccessory';
import { isWeb } from 'shared/constants/platform';
import { PressableAnalyticsProps } from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';

import TaskListItemTextStack from './TaskListItemTextStack';

interface TaskListItemProps extends PressableAnalyticsProps {
  primaryCopy: string;
  secondaryCopy?: Maybe<string>;
  leadingAccessory?: Maybe<ListItemLeadingAccessoryProps>;
  isDisabled?: Maybe<boolean>;
  accessibilityLabel?: string;
  onPress?: Maybe<() => void> | null;
  isStepCompleted?: Maybe<boolean>;
}

const computeStyle = (
  isHovering,
  isFocused,
  isPressed,
  isDisabled,
  isStepCompleted,
): ViewStyle => {
  if (isDisabled) {
    return styles.container;
  } else if (isStepCompleted) {
    return styles.isStepCompleted;
  }
  if (isPressed) {
    return styles.containerPressed;
  } else if (isHovering) {
    return styles.containerHover;
  } else if (isFocused) {
    return styles.containerFocus;
  }

  return styles.container;
};

const ListItemContent = ({
  primaryCopy,
  secondaryCopy,
  leadingAccessory,
  isDisabled = false,
  isStepCompleted = false,
}: TaskListItemProps) => {
  const textProps = {
    primaryCopy,
    secondaryCopy,
    isDisabled,
    isStepCompleted,
  };
  const chevronColor = isDisabled
    ? theme.color.interactive.disabled.text
    : theme.color.text.primary;

  return (
    <View style={styles.contentContainer}>
      {leadingAccessory ? (
        <ListItemLeadingAccessory
          accessory={leadingAccessory}
          isDisabled={Boolean(isDisabled)}
        />
      ) : null}
      <View style={styles.listItemTextContainer}>
        <TaskListItemTextStack {...textProps} />
      </View>
      {!isStepCompleted ? (
        <View style={styles.chevronContainer} testID="list-item-chevron">
          <Icon iconKey="CHEVRON_RIGHT" iconColor={chevronColor} />
        </View>
      ) : null}
    </View>
  );
};

const TaskListItem = (props: TaskListItemProps) => {
  const {
    onPress,
    isDisabled = false,
    isStepCompleted = false,
    accessibilityLabel,
    analyticsId = '',
    customAttributes = null,
  } = props;
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  if (onPress) {
    return (
      <PressableWithDelay
        analyticsId={analyticsId}
        customAttributes={customAttributes}
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        onBlur={() => setIsFocused(false)}
        onFocus={() => setIsFocused(true)}
        onPressIn={() => setIsPressed(true)}
        onPressOut={() => setIsPressed(false)}
        onPress={() => {
          onPress();
          if (isWeb) {
            setIsFocused(false);
          }
        }}
        disabled={Boolean(isDisabled)}
        aria-disabled={Boolean(isDisabled)}
        accessible={false}
        accessibilityLabel={accessibilityLabel}
        role="button"
        style={[
          styles.button,
          computeStyle(
            isHovered,
            isFocused,
            isPressed,
            isDisabled,
            isStepCompleted,
          ),
        ]}
        testID="PressableTaskListItem"
      >
        <ListItemContent {...props} />
      </PressableWithDelay>
    );
  }
  return (
    <View
      style={styles.container}
      aria-disabled={Boolean(isDisabled)}
      accessibilityLabel={accessibilityLabel}
      testID="StaticListItem"
    >
      <ListItemContent {...props} />
    </View>
  );
};

const styles = StyleSheet.create({
  button: {
    borderWidth: 0,
    ...Platform.select({
      web: {
        outlineStyle: 'solid',
        outlineWidth: 0,
      },
    }),
  },
  chevronContainer: {
    height: 24,
    width: 24,
  },
  container: {
    backgroundColor: theme.color.interactive.tertiary,
    borderRadius: theme.spacing.xs,
  },
  containerFocus: {
    borderWidth: 1,
  },
  containerHover: {
    backgroundColor: theme.color.interactive.tertiaryHovered,
  },
  containerPressed: {
    backgroundColor: theme.color.interactive.tertiaryPressed,
  },
  contentContainer: {
    alignItems: 'center',
    columnGap: theme.spacing.m,
    flexDirection: 'row',
    margin: theme.spacing.m,
  },
  isStepCompleted: {
    pointerEvents: 'none',
  },
  listItemTextContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    flexGrow: 1,
    flexShrink: 1,
    flexWrap: 'wrap',
    minWidth: 100,
    overflow: 'hidden',
  },
});

export default TaskListItem;
